import { useEffect, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { reportService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { AreaTrend, BarTrend, DonutChart, ChartLegend } from "@/components/common/Charts";
import { KPICard } from "@/components/common/KPICard";
import { SkeletonCard } from "@/components/common/Skeletons";
import { fmtKm, fmtL, fmtDuration, clockTime, shortDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileDown, Printer, Gauge, Users, Route, Fuel, ShieldCheck, Bell, Wrench, Activity, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { id: "fleet-utilization", name: "Fleet Utilization", icon: Activity },
  { id: "vehicle-activity", name: "Vehicle Activity", icon: Gauge },
  { id: "driver-activity", name: "Driver Activity", icon: Users },
  { id: "trip-summary", name: "Trip Summary", icon: Route },
  { id: "fuel-usage", name: "Fuel Usage", icon: Fuel },
  { id: "safety", name: "Safety", icon: ShieldCheck },
  { id: "alerts", name: "Alerts", icon: Bell },
  { id: "vehicle-health", name: "Vehicle Health", icon: Wrench },
];

export default function Reports() {
  const [range, setRange] = useState("7d");
  const [active, setActive] = useState("fleet-utilization");
  const [exporting, setExporting] = useState(false);
  const category = CATEGORIES.find((c) => c.id === active);
  const isPeriodBased = active !== "vehicle-health"; // matches POINT_IN_TIME_REPORTS on the backend

  const { data: result, loading } = useFetch(
    () => reportService.get(active, range),
    [active, isPeriodBased ? range : null]
  );
  // useFetch doesn't clear its stale `data` while a new fetch is in flight,
  // and there's a render in between `active` changing and `loading` flipping
  // back to true — without this check, a report view can briefly receive
  // the *previous* report's differently-shaped data (e.g. an object where
  // it expects an array) and crash.
  const data = result?.report === active ? result.data : null;

  const exp = async (format) => {
    setExporting(true);
    try {
      await reportService.export(active, format.toLowerCase(), range);
      toast.success(`${category.name} exported as ${format}`);
    } catch (err) {
      toast.error(err.response?.data?.error || `Could not export as ${format}`);
    } finally {
      setExporting(false);
    }
  };

  const printReport = async () => {
    setExporting(true);
    try {
      await reportService.printPdf(active, range);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not open report for printing");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Reports"
        subtitle="Analyze fleet performance, utilization and safety over time."
        actions={
          <>
            <Button variant="secondary" disabled={exporting} onClick={() => exp("CSV")} data-testid="export-csv"><FileDown className="mr-1.5 h-4 w-4" /> CSV</Button>
            <Button variant="secondary" disabled={exporting} onClick={printReport} data-testid="export-pdf"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
          </>
        }
      />

      <div className="mb-6 flex flex-wrap gap-2">
        <Select value={range} onValueChange={setRange} disabled={!isPeriodBased}>
          <SelectTrigger className="h-10 w-full sm:w-40" data-testid="report-range"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7d">Last 7 days</SelectItem><SelectItem value="30d">Last 30 days</SelectItem><SelectItem value="90d">Last 90 days</SelectItem></SelectContent>
        </Select>
        {!isPeriodBased && <span className="flex items-center text-xs text-muted-foreground">Vehicle Health reflects current state, not a time window.</span>}
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="space-y-1.5">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`report-cat-${c.id}`}
              className={cn("flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition", active === c.id ? "border-primary/40 bg-primary/10 text-foreground" : "border-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground")}>
              <c.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{c.name}</span>
              {active === c.id && <ChevronRight className="h-4 w-4" />}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {loading || !data ? (
            <div className="grid gap-4 md:grid-cols-2"><SkeletonCard className="h-64" /><SkeletonCard className="h-64" /></div>
          ) : (
            <ReportBody reportKey={active} data={data} />
          )}
        </div>
      </div>
    </div>
  );
}

function ReportBody({ reportKey, data }) {
  switch (reportKey) {
    case "fleet-utilization": return <FleetUtilizationView data={data} />;
    case "vehicle-activity": return <VehicleActivityView rows={data} />;
    case "driver-activity": return <DriverActivityView rows={data} />;
    case "trip-summary": return <TripSummaryView rows={data} />;
    case "fuel-usage": return <FuelUsageView data={data} />;
    case "safety": return <SafetyView data={data} />;
    case "alerts": return <AlertsReportView data={data} />;
    case "vehicle-health": return <VehicleHealthView data={data} />;
    default: return null;
  }
}

function FleetUtilizationView({ data }) {
  const statusData = Object.entries(data.fleet.status_breakdown).map(([name, value]) => ({ name, value }));
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Trips" value={data.totals.trips} icon={Route} />
        <KPICard label="Distance" value={fmtKm(data.totals.distance_km)} icon={Gauge} />
        <KPICard label="Fuel Used" value={fmtL(data.totals.fuel_used_l)} icon={Fuel} />
        <KPICard label="Safety Events" value={data.totals.safety_events} icon={ShieldCheck} accent />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-4 font-display text-base font-medium">Fleet status</h3>
          <DonutChart data={statusData} centerValue={`${data.fleet.utilization_pct}%`} centerLabel="Utilized" height={200} />
          <div className="mt-4"><ChartLegend data={statusData} /></div>
        </Card>
        <Card className="border-border/70 bg-card/60 p-5 space-y-3">
          <h3 className="font-display text-base font-medium">Utilization</h3>
          <StatRow label="Total vehicles" value={data.fleet.total_vehicles} />
          <StatRow label="Used this period" value={data.fleet.vehicles_used_in_period} />
          <StatRow label="Unused this period" value={data.fleet.vehicles_unused_in_period} />
          <StatRow label="Currently in use" value={data.fleet.currently_in_use} />
          <StatRow label="Idle hours (fleet-wide)" value={data.totals.idle_hours} />
        </Card>
      </div>
    </>
  );
}

function VehicleActivityView({ rows }) {
  return (
    <ReportTable
      empty="No vehicle activity in this period."
      head={["Vehicle", "Plate", "Status", "Trips", "Distance", "Fuel", "Idle", "Max Speed"]}
      rows={rows}
      render={(r) => [
        r.vehicle, r.license_plate || "—", r.status, r.trips, fmtKm(r.distance_km),
        fmtL(r.fuel_used_l), `${r.idle_hours} h`, `${(r.max_speed ?? 0).toFixed(1)} mph`,
      ]}
    />
  );
}

function DriverActivityView({ rows }) {
  return (
    <ReportTable
      empty="No driver activity in this period."
      head={["Driver", "Status", "Trips", "Distance", "Driving Hours", "Hard Braking", "Hard Accel", "Safety Score"]}
      rows={rows}
      render={(r) => [
        r.driver, r.status, r.trips, fmtKm(r.distance_km), `${r.driving_hours} h`,
        r.hard_braking, r.hard_acceleration, r.safety_score ?? "—",
      ]}
    />
  );
}

function TripSummaryView({ rows }) {
  return (
    <ReportTable
      empty="No trips in this period."
      head={["Vehicle", "Driver", "Start", "Distance", "Duration", "Max Speed", "Braking", "Accel"]}
      rows={rows}
      render={(r) => [
        r.vehicle || "—", r.driver || "Unassigned",
        r.start_time ? `${shortDate(r.start_time, r.utc_offset_minutes)} ${clockTime(r.start_time, r.utc_offset_minutes)}` : "—",
        fmtKm(r.distance_km), fmtDuration(r.duration_minutes), r.max_speed ? `${r.max_speed.toFixed(1)} mph` : "—",
        r.hard_braking, r.hard_acceleration,
      ]}
    />
  );
}

function FuelUsageView({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Total Fuel Used" value={fmtL(data.total_fuel_l)} icon={Fuel} accent />
      </div>
      {data.daily_series.length > 0 && (
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-4 font-display text-base font-medium">Fuel used per day</h3>
          <AreaTrend data={data.daily_series} x="date" y="fuel_used_l" unit="gal" />
        </Card>
      )}
      <ReportTable
        empty="No fuel usage in this period."
        head={["Vehicle", "Fuel Used", "Distance", "Gal/100mi", "Idle"]}
        rows={data.per_vehicle}
        render={(r) => [
          r.vehicle, fmtL(r.fuel_used_l), fmtKm(r.distance_km),
          r.consumption_l_per_100km ?? "—", `${r.idle_hours} h`,
        ]}
      />
    </>
  );
}

function SafetyView({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Hard Braking" value={data.totals.hard_braking} icon={ShieldCheck} />
        <KPICard label="Hard Acceleration" value={data.totals.hard_acceleration} icon={ShieldCheck} />
        <KPICard label="Peak Speed" value={`${data.totals.peak_speed} mph`} icon={Gauge} />
        <KPICard label="Events / 100mi" value={data.totals.events_per_100km ?? "—"} icon={ShieldCheck} accent />
      </div>
      {data.daily_series.length > 0 && (
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-4 font-display text-base font-medium">Safety events over time</h3>
          <BarTrend data={data.daily_series} x="date" y="total" color="hsl(var(--primary))" />
        </Card>
      )}
    </>
  );
}

function AlertsReportView({ data }) {
  const severityData = Object.entries(data.by_severity).map(([name, value]) => ({ name, value }));
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Total Alerts" value={data.total} icon={Bell} accent />
        <KPICard label="Mean Time to Resolve" value={data.mean_time_to_resolve_hours != null ? `${data.mean_time_to_resolve_hours} h` : "—"} icon={ShieldCheck} />
      </div>
      {severityData.length > 0 && (
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-4 font-display text-base font-medium">By severity</h3>
          <DonutChart data={severityData} centerValue={String(data.total)} centerLabel="Total" height={180} />
          <div className="mt-4"><ChartLegend data={severityData} /></div>
        </Card>
      )}
      <ReportTable
        empty="No alerts in this period."
        head={["Type", "Category", "Count", "Resolved", "Still New"]}
        rows={data.by_type}
        render={(r) => [r.type, r.category, r.count, r.resolved, r.still_new]}
      />
    </>
  );
}

function VehicleHealthView({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Total Vehicles" value={data.summary.total_vehicles} icon={Gauge} />
        <KPICard label="Needing Attention" value={data.summary.needing_attention} icon={Wrench} accent />
        <KPICard label="Disconnected Devices" value={data.summary.disconnected_devices} icon={Bell} />
      </div>
      <ReportTable
        empty="No vehicles found."
        head={["Vehicle", "Status", "Battery", "Fuel", "Odometer", "Open Alerts", "Needs Attention"]}
        rows={data.vehicles}
        render={(r) => [
          // battery_status, not battery_pct: Bouncie's hardware never
          // populates a percentage, so that column printed a dash for every
          // vehicle. See backend/app/services/battery.py.
          r.vehicle || "—", r.status,
          BATTERY_CELL[r.battery_status] || BATTERY_CELL.unknown,
          r.fuel_pct != null ? `${r.fuel_pct}%` : "—", fmtKm(r.odometer_km),
          r.open_health_alerts, r.needs_attention ? "Yes" : "No",
        ]}
      />
    </>
  );
}

// The four states the vehicles API and this report both report.
const BATTERY_CELL = { good: "Good", low: "Low", critical: "Critical", unknown: "Unknown" };

function ReportTable({ head, rows, render, empty }) {
  if (!rows || rows.length === 0) return <Card className="border-border/70 bg-card/60 p-8 text-center text-sm text-muted-foreground">{empty}</Card>;
  return (
    <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent border-border/60">{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.vehicle_id || r.driver_id || r.trip_id || r.type || i} className="border-border/50">
                {render(r).map((cell, j) => <TableCell key={j} className={j === 0 ? "font-medium" : "font-telemetry text-sm"}>{cell}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-telemetry font-medium">{value}</span></div>;
}
